/**
 * Dot navigation for the product media slider (mobile).
 *
 * Renders one dot per visible slide of the closest <slider-component>, follows
 * Dawn's `slideChanged` event and scrolls to a slide when its dot is tapped.
 * The slider itself (swipe, snap, variant media switching) stays Dawn's.
 */
class GalleryDots extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.closest('slider-component');
    if (!this.sliderComponent) return;

    this.onSlideChanged = (event) => this.setActive(event.detail.currentPage - 1);
    this.sliderComponent.addEventListener('slideChanged', this.onSlideChanged);

    customElements.whenDefined('slider-component').then(() => requestAnimationFrame(() => this.build()));
  }

  disconnectedCallback() {
    if (this.sliderComponent) this.sliderComponent.removeEventListener('slideChanged', this.onSlideChanged);
  }

  get slides() {
    const component = this.sliderComponent;
    if (component.sliderItemsToShow && component.sliderItemsToShow.length) return Array.from(component.sliderItemsToShow);
    return Array.from(component.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);
  }

  build() {
    const slides = this.slides;
    this.innerHTML = '';

    if (slides.length < 2) {
      this.hidden = true;
      return;
    }

    slides.forEach((slide, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gallery-dots__dot';
      dot.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
      dot.addEventListener('click', () => this.goTo(index));
      this.appendChild(dot);
    });

    this.hidden = false;
    this.setActive((this.sliderComponent.currentPage || 1) - 1);
  }

  goTo(index) {
    const component = this.sliderComponent;
    const list = component.slider;
    if (!list) return;
    const offset = component.sliderItemOffset || list.clientWidth;
    list.scrollTo({ left: offset * index, behavior: 'smooth' });
  }

  setActive(index) {
    this.querySelectorAll('.gallery-dots__dot').forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('gallery-dots')) {
  customElements.define('gallery-dots', GalleryDots);
}
